// Binds a MediaStreamTrack (or a whole MediaStream) to a <video> element.
// React does not manage a video element's srcObject, so this is imperative
// on purpose — a plain effect keyed on the track/stream identity.
//
// The picture's size never depends on WebKit honoring object-fit. On iOS it
// kept painting a small picture inside the box after the frame size changed,
// after a declined phone call, and after a return to the page (QUEUE rows
// 425, 426, and 430; Adam: "the thing that happened when i got a call and it
// was meant to refill the screen is not fixed, and it even sometimes becomes
// small in this window"). So the video sits in a clipping box and gets an
// explicit pixel size worked out here from its own frame size, and the stream
// is attached again whenever the frame size changes, the camera comes back, or
// the page is shown again, which gives WebKit a fresh picture layer instead of
// the stale one.
//
// The frame size comes from the frames themselves (requestVideoFrameCallback)
// when the browser offers it, and object-fit stays on as well. After a
// declined call iOS kept reporting the old videoWidth and videoHeight while the
// camera sent a portrait picture, and with object-fit fill the face was
// stretched flat (row 438; Adam: "What in the world is this flattened video").
// A wrong size now crops at worst, never distorts.
//
// What it was (row 445; Adam: "Hung up the call and the picture still goes
// small here. Don't guess"). His screenshot measured the picture at 219 by
// 390 CSS pixels, 139 down, in a 390 by 693 element: exactly a 16:9 picture
// fitted inside the element and then turned a quarter. After the declined
// call iOS hands WebKit the camera frames sideways with a rotation flag, and
// WebKit fits its picture layer to the old sideways size (the one it still
// reports as videoWidth and videoHeight) before turning it. So when the
// reported size and the painted frames disagree on which way is up, the
// element is given the sideways shape instead, sized so the turned picture
// covers the box (layerBox). Each change of that state is reported through
// onReport, so the drops log carries the numbers from the phone itself.

import { useEffect, useRef, type JSX } from "react";
import { keepPlaying } from "../lib/keepPlaying";

interface VideoViewProps {
  readonly stream?: MediaStream | null;
  readonly track?: MediaStreamTrack | null;
  readonly mirrored?: boolean;
  readonly fit?: "cover" | "contain";
  readonly muted?: boolean;
  readonly className?: string;
  /** The picture's width over its height, each time it is known or changes
   * (a phone turned on its side sends a new size). */
  readonly onAspect?: (ratio: number) => void;
  /** Each change in how the picture is laid out on WebKit, for the drops log. */
  readonly onReport?: (report: VideoFitReport) => void;
}

/** What the drops log keeps about a WebKit self-view layout (row 445). */
export interface VideoFitReport {
  readonly event: "fit";
  readonly reason: string;
  readonly rotated: boolean;
  readonly vw: number;
  readonly vh: number;
  readonly fw: number;
  readonly fh: number;
  readonly sw: number;
  readonly sh: number;
  readonly bw: number;
  readonly bh: number;
  readonly ew: number;
  readonly eh: number;
}

/** The video's box inside a w x h frame for a picture of vw x vh. */
export function fitBox(w: number, h: number, vw: number, vh: number, fit: "cover" | "contain"): { width: number; height: number; left: number; top: number } {
  if (!(w > 0 && h > 0 && vw > 0 && vh > 0)) return { width: w, height: h, left: 0, top: 0 };
  const scale = fit === "cover" ? Math.max(w / vw, h / vh) : Math.min(w / vw, h / vh);
  const width = Math.ceil(vw * scale);
  const height = Math.ceil(vh * scale);
  return { width, height, left: Math.round((w - width) / 2), top: Math.round((h - height) / 2) };
}

/** The video element's box when WebKit reports the picture's size (vw x vh)
 * the other way up from the frames it paints (fw x fh). WebKit then fits a
 * picture of the reported shape inside the element and turns it a quarter, so
 * the element gets that shape, sized so the turned picture fills the box the
 * way `fit` asks. Null when the two agree, or either is unknown. */
export function layerBox(
  w: number,
  h: number,
  vw: number,
  vh: number,
  fw: number,
  fh: number,
  fit: "cover" | "contain",
): { width: number; height: number; left: number; top: number } | null {
  if (!(w > 0 && h > 0 && vw > 0 && vh > 0 && fw > 0 && fh > 0)) return null;
  if (vw === vh || fw === fh || vw > vh === fw > fh) return null;
  // The element is vw:vh. Turned, the picture is (L * vh / vw) wide and L tall.
  const ratio = vw / vh;
  const long = fit === "cover" ? Math.max(h, w * ratio) : Math.min(h, w * ratio);
  const width = Math.ceil(long);
  const height = Math.ceil(long / ratio);
  return { width, height, left: Math.round((w - width) / 2), top: Math.round((h - height) / 2) };
}

// Every browser on iOS is WebKit; Chrome and Edge elsewhere never showed the
// stale picture, so they skip the re-attach and its blank frame.
const WEBKIT = typeof navigator !== "undefined" && /AppleWebKit/.test(navigator.userAgent) && !/Chrome\/|Chromium\/|Edg\//.test(navigator.userAgent);

export function VideoView({ stream, track, mirrored, fit = "cover", muted, className, onAspect, onReport }: VideoViewProps): JSX.Element {
  const boxRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLVideoElement>(null);
  const holdRef = useRef<HTMLCanvasElement>(null);
  const aspectRef = useRef(onAspect);
  aspectRef.current = onAspect;
  const reportRef = useRef(onReport);
  reportRef.current = onReport;
  const fitRef = useRef(fit);
  fitRef.current = fit;

  useEffect(() => {
    const el = ref.current;
    const box = boxRef.current;
    if (!el || !box) return;
    // iOS Safari only autoplays a video it already sees as muted, and the
    // property has to be set before play(), not left to the attribute.
    el.muted = !!muted;
    const source = stream ?? (track ? new MediaStream([track]) : null);
    el.srcObject = source;
    // The last picture of the track before this one stays up until this one
    // paints its first frame: a re-pull of a frozen camera swapped in a
    // track that showed black for seconds (QUEUE row 452).
    const hold = holdRef.current;
    let holdTimer: ReturnType<typeof setTimeout> | undefined;
    const release = (): void => {
      if (holdTimer) clearTimeout(holdTimer);
      holdTimer = undefined;
      if (hold && hold.style.display !== "none") hold.style.display = "none";
    };
    if (!source) release();
    else if (hold && hold.style.display !== "none") holdTimer = setTimeout(release, 15_000);
    const play = (): void =>
      void el.play().catch(() => {
        // Autoplay can be blocked before a user gesture; the element still
        // shows the first frame once it is. A phone call that pauses it is
        // followed up by keepPlaying.
      });
    play();

    let lastAspect = 0;
    // The size of the last frame actually painted, which WebKit keeps current
    // when videoWidth and videoHeight go stale.
    let frameW = 0;
    let frameH = 0;
    let reason = "attach";
    let lastReport = "";
    let reports = 0;
    const layout = (): void => {
      const vw = frameW || el.videoWidth;
      const vh = frameH || el.videoHeight;
      if (vw > 0 && vh > 0 && vw / vh !== lastAspect) {
        lastAspect = vw / vh;
        aspectRef.current?.(lastAspect);
      }
      const bw = box.clientWidth;
      const bh = box.clientHeight;
      const turned = WEBKIT ? layerBox(bw, bh, el.videoWidth, el.videoHeight, frameW, frameH, fitRef.current) : null;
      const { width, height, left, top } = turned ?? fitBox(bw, bh, vw, vh, fitRef.current);
      const s = el.style;
      const px = (n: number): string => `${n}px`;
      if (s.width !== px(width)) s.width = px(width);
      if (s.height !== px(height)) s.height = px(height);
      if (s.left !== px(left)) s.left = px(left);
      if (s.top !== px(top)) s.top = px(top);
      if (WEBKIT && reportRef.current && el.videoWidth > 0) {
        const set = source?.getVideoTracks()[0]?.getSettings();
        const r: VideoFitReport = {
          event: "fit",
          reason,
          rotated: !!turned,
          vw: el.videoWidth,
          vh: el.videoHeight,
          fw: frameW,
          fh: frameH,
          sw: set?.width ?? 0,
          sh: set?.height ?? 0,
          bw,
          bh,
          ew: width,
          eh: height,
        };
        // A change of shape, not of the box: the box moves with the trivia
        // card and the keyboard, and those are not what this is for.
        const key = `${r.rotated}|${r.vw}x${r.vh}|${r.fw}x${r.fh}|${r.sw}x${r.sh}`;
        if (key !== lastReport && reports < 20) {
          lastReport = key;
          reports++;
          reportRef.current(r);
        }
      }
    };

    // Attach the same source again: WebKit builds a new picture layer for it,
    // at the size the camera is sending now. At most once a second, so the
    // loadedmetadata and resize it causes cannot start a loop.
    let lastAttach = 0;
    let lastSize = "";
    const reattach = (): void => {
      if (!WEBKIT || !source || Date.now() - lastAttach < 1000) return;
      if (!source.getVideoTracks().some((t) => t.readyState === "live")) return;
      lastAttach = Date.now();
      el.srcObject = null;
      el.srcObject = source;
      play();
    };
    const timers: ReturnType<typeof setTimeout>[] = [];
    const later = (fn: () => void, ms: number[]): void => {
      for (const t of timers.splice(0)) clearTimeout(t);
      for (const m of ms) timers.push(setTimeout(fn, m));
    };
    const onFrameSize = (e: Event): void => {
      reason = e.type;
      layout();
      const size = `${el.videoWidth}x${el.videoHeight}`;
      if (el.videoWidth === 0 || size === lastSize) return;
      const first = lastSize === "";
      lastSize = size;
      // The first size is the attach that just happened; any later change is
      // the stale-paint case.
      if (!first) reattach();
    };
    // A phone call on the same phone mutes the camera and pauses the video,
    // and the picture came back small when the call went away (row 430).
    const onCamera = (e: Event): void => {
      reason = `camera ${e.type}`;
      play();
      layout();
      later(() => {
        reattach();
        layout();
      }, [300, 1200]);
    };
    // Coming back to the page brought the small picture back too (row 426).
    let hiddenAt = 0;
    const onVisibility = (): void => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      reason = "page shown";
      layout();
      if (hiddenAt) {
        hiddenAt = 0;
        reattach();
        later(layout, [120, 600]);
      }
    };
    const onPageShow = (e: PageTransitionEvent): void => {
      if (e.persisted) reattach();
      layout();
    };

    type FrameMeta = { width: number; height: number };
    type FrameVideo = HTMLVideoElement & {
      requestVideoFrameCallback?: (cb: (now: number, meta: FrameMeta) => void) => number;
      cancelVideoFrameCallback?: (id: number) => void;
    };
    const fv = el as FrameVideo;
    let frameCb = 0;
    let frameCount = 0;
    const onFrame = (_now: number, meta: FrameMeta): void => {
      release();
      if (meta.width > 0 && meta.height > 0 && (meta.width !== frameW || meta.height !== frameH)) {
        reason = "frame size";
        frameW = meta.width;
        frameH = meta.height;
        layout();
      } else if (++frameCount % 30 === 0) {
        layout();
      }
      frameCb = fv.requestVideoFrameCallback?.(onFrame) ?? 0;
    };
    frameCb = fv.requestVideoFrameCallback?.(onFrame) ?? 0;

    el.addEventListener("loadedmetadata", onFrameSize);
    el.addEventListener("resize", onFrameSize);
    el.addEventListener("playing", layout);
    // Without frame callbacks, the first size is the first frame.
    const onSized = (): void => {
      if (!fv.requestVideoFrameCallback && el.videoWidth > 0) release();
    };
    el.addEventListener("loadeddata", onSized);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", layout);
    const observer = new ResizeObserver(layout);
    observer.observe(box);
    const tracks = source ? source.getVideoTracks() : [];
    for (const t of tracks) {
      t.addEventListener("mute", onCamera);
      t.addEventListener("unmute", onCamera);
    }
    layout();
    const stopKeeping = keepPlaying(el);
    return () => {
      stopKeeping();
      // Keep what is on screen now, for the track that replaces this one.
      if (hold && el.videoWidth > 0 && el.readyState >= 2) {
        try {
          hold.width = el.videoWidth;
          hold.height = el.videoHeight;
          hold.getContext("2d")?.drawImage(el, 0, 0, hold.width, hold.height);
          const s = el.style;
          Object.assign(hold.style, { display: "block", width: s.width, height: s.height, left: s.left, top: s.top, transform: s.transform });
        } catch {
          hold.style.display = "none";
        }
      }
      if (holdTimer) clearTimeout(holdTimer);
      el.removeEventListener("loadeddata", onSized);
      if (frameCb) fv.cancelVideoFrameCallback?.(frameCb);
      observer.disconnect();
      el.removeEventListener("loadedmetadata", onFrameSize);
      el.removeEventListener("resize", onFrameSize);
      el.removeEventListener("playing", layout);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", layout);
      for (const t of tracks) {
        t.removeEventListener("mute", onCamera);
        t.removeEventListener("unmute", onCamera);
      }
      for (const t of timers) clearTimeout(t);
    };
  }, [stream, track, muted]);

  // A new fit with the same stream only needs the box worked out again.
  useEffect(() => {
    const el = ref.current;
    const box = boxRef.current;
    // On WebKit the layout above runs again within a second of frames and
    // knows whether the picture is turned, which this does not.
    if (!el || !box || WEBKIT) return;
    const { width, height, left, top } = fitBox(box.clientWidth, box.clientHeight, el.videoWidth, el.videoHeight, fit);
    Object.assign(el.style, { width: `${width}px`, height: `${height}px`, left: `${left}px`, top: `${top}px` });
  }, [fit]);

  return (
    <div
      ref={boxRef}
      className={className}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        background: fit === "contain" ? "#10151b" : undefined,
      }}
    >
      <video
        ref={ref}
        autoPlay
        playsInline
        muted={muted}
        style={{
          position: "absolute",
          display: "block",
          left: 0,
          top: 0,
          width: "100%",
          height: "100%",
          maxWidth: "none",
          objectFit: fit,
          transform: mirrored ? "scaleX(-1)" : undefined,
        }}
      />
      <canvas
        ref={holdRef}
        aria-hidden
        style={{
          position: "absolute",
          display: "none",
          left: 0,
          top: 0,
          width: "100%",
          height: "100%",
          objectFit: fit,
          pointerEvents: "none",
        }}
      />
    </div>
  );
}
